/**
 * Rapid grading — the focus mode behind /team-leader/exams/review.
 *
 * The per-exam `GradeEssayDialog` already advances between the answers of ONE attempt. What it
 * cannot do is cross the boundary between students: finishing an attempt closes the dialog and the
 * grader goes back to a list to pick the next one. This component takes an ordered run of sessions
 * and walks answers *and* students in a single line, so a queue of 14 answers spread over 6 students
 * is one continuous pass rather than 6 open/close cycles.
 *
 * It writes through exactly the same endpoint as the dialog (`updateExamResponseGrade` →
 * POST /admin/grading-queue/{responseId}/grade), so the server-side grading-scope check is
 * unchanged and this surface can never mark something the dialog could not.
 *
 * The session run is a SNAPSHOT passed in by the caller, deliberately not a live query. Grading the
 * last essay of an attempt flips its session Completed, which drops it out of the queue endpoint —
 * so a live list would delete the row out from under the grader mid-pass and renumber everything
 * after it. The snapshot keeps "student 3 of 6" true for the whole run; the caller refetches the
 * real queue when the run ends.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  SkipForward,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { TableSkeleton } from "@/components/loading";
import { keys } from "@/hooks/use-tl-exams";
import { fetchSessionResponses, updateExamResponseGrade } from "@/lib/api/exams";
import type { GradingQueueItem, GradingResponse } from "@/lib/types";

const studentLabelOf = (s: GradingQueueItem) => s.studentName || s.studentEmail;

export function RapidGradingFlow({
  sessions,
  onExit,
}: {
  /** Ordered snapshot of the run. Never re-derived from a live query — see the file header. */
  sessions: GradingQueueItem[];
  onExit: () => void;
}) {
  const qc = useQueryClient();
  const [sessionIndex, setSessionIndex] = useState(0);
  const [activeResponseId, setActiveResponseId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { points: string; note: string }>>({});
  /**
   * Response ids marked during THIS run. The list query is invalidated on every save, but a refetch
   * is not instant, so "what is still pending" is answered from here first — otherwise a fast
   * grader saves, advances, and lands back on the answer they just marked because the refetch had
   * not landed yet.
   */
  const [justGraded, setJustGraded] = useState<Set<string>>(new Set());
  const [markedTotal, setMarkedTotal] = useState(0);
  const pointsRef = useRef<HTMLInputElement>(null);

  const session: GradingQueueItem | undefined = sessions[sessionIndex];

  const {
    data: responses = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["tl-exams", "session-grading", session?.sessionId],
    queryFn: () => fetchSessionResponses(session!.sessionId),
    enabled: !!session?.sessionId,
  });

  // Auto-graded answers are not the grader's business here; this surface exists to clear the manual
  // backlog, so it shows only what a human can mark.
  const gradable = useMemo(
    () => (responses as GradingResponse[]).filter((r) => r.gradingStatus !== "AutoGraded"),
    [responses],
  );

  const isPending = useCallback(
    (r: GradingResponse) => r.gradingStatus === "PendingManualGrade" && !justGraded.has(r.responseId),
    [justGraded],
  );

  // Land on the first answer still needing a mark whenever the student changes or the paper loads.
  useEffect(() => {
    if (gradable.length === 0) return;
    if (activeResponseId && gradable.some((r) => r.responseId === activeResponseId)) return;
    const firstPending = gradable.find(isPending) ?? gradable[0];
    setActiveResponseId(firstPending.responseId);
  }, [gradable, activeResponseId, isPending]);

  const activeIndex = gradable.findIndex((r) => r.responseId === activeResponseId);
  const active: GradingResponse | undefined = activeIndex >= 0 ? gradable[activeIndex] : undefined;

  // Typing the score is the fast path, so the field takes focus on every new answer — the grader
  // reads, types a number, hits Enter, and never touches the mouse.
  useEffect(() => {
    if (active) pointsRef.current?.focus();
  }, [active?.responseId]);

  const baseDraft = (r: GradingResponse) => ({
    points: r.pointsAwarded != null ? String(r.pointsAwarded) : "",
    note: r.graderNote ?? "",
  });
  const draft = active ? (drafts[active.responseId] ?? baseDraft(active)) : { points: "", note: "" };
  const patchDraft = (patch: Partial<{ points: string; note: string }>) => {
    if (!active) return;
    setDrafts((d) => ({
      ...d,
      [active.responseId]: { ...(d[active.responseId] ?? baseDraft(active)), ...patch },
    }));
  };

  const goToSession = useCallback((index: number) => {
    setSessionIndex(index);
    setActiveResponseId(null);
  }, []);

  /** Next unmarked answer for this student, else the next student, else end the run. */
  const advance = useCallback(() => {
    const nextHere = gradable.find((r, i) => i > activeIndex && isPending(r));
    if (nextHere) {
      setActiveResponseId(nextHere.responseId);
      return;
    }
    if (sessionIndex < sessions.length - 1) {
      goToSession(sessionIndex + 1);
      return;
    }
    toast.success("Queue cleared — nothing left to mark.");
    onExit();
  }, [gradable, activeIndex, isPending, sessionIndex, sessions.length, goToSession, onExit]);

  const grade = useMutation({
    mutationFn: (vars: { responseId: string; points: number; note: string }) =>
      updateExamResponseGrade(vars.responseId, { pointsEarned: vars.points, note: vars.note }),
    onSuccess: (_data, vars) => {
      setJustGraded((s) => new Set(s).add(vars.responseId));
      setMarkedTotal((n) => n + 1);
      qc.invalidateQueries({ queryKey: ["tl-exams", "session-grading", session?.sessionId] });
      // Both the per-exam queues and this page's cross-exam queue live under this prefix, so the
      // exam workspace is correct the moment the grader navigates back to it.
      qc.invalidateQueries({ queryKey: keys.gradingQueueRoot });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Failed to save grade"),
  });

  const maxPoints = active?.points ?? 0;

  const save = useCallback(async () => {
    if (!active) return;
    const raw = (drafts[active.responseId] ?? baseDraft(active)).points;
    if (raw.trim() === "") {
      toast.error("Enter the points awarded before saving.");
      pointsRef.current?.focus();
      return;
    }
    const points = Number(raw);
    // Guarded here as well as by the input's min/max: those attributes do not stop a pasted or
    // keyboard-incremented value, and an over-max award silently inflates the attempt's total.
    if (!Number.isFinite(points) || points < 0 || points > maxPoints) {
      toast.error(`Award between 0 and ${maxPoints} points.`);
      pointsRef.current?.focus();
      return;
    }
    try {
      await grade.mutateAsync({
        responseId: active.responseId,
        points,
        note: (drafts[active.responseId] ?? baseDraft(active)).note,
      });
      advance();
    } catch {
      /* onError already reported it; stay put so the mark is not lost */
    }
  }, [active, drafts, maxPoints, grade, advance]);

  // Escape leaves the run. Ctrl/Cmd+Enter saves from anywhere, including the feedback box where a
  // bare Enter has to keep inserting newlines.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onExit();
        return;
      }
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onExit, save]);

  if (!session) {
    return (
      <Card>
        <CardContent className="p-10 text-center space-y-3">
          <CheckCircle2 className="mx-auto h-10 w-10 text-success/50" />
          <p className="text-sm text-muted-foreground">Nothing left in this run.</p>
          <Button variant="outline" onClick={onExit}>
            Back to queue
          </Button>
        </CardContent>
      </Card>
    );
  }

  const pendingHere = gradable.filter(isPending).length;

  return (
    <div className="space-y-3">
      {/* Run header — where the grader is in the whole pass, not just this paper. */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{studentLabelOf(session)}</p>
            <p className="text-xs text-muted-foreground truncate">
              {session.examTitle} · student {sessionIndex + 1} of {sessions.length}
              {pendingHere > 0 ? ` · ${pendingHere} left on this paper` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="text-[10px]">
              {markedTotal} marked this run
            </Badge>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={sessionIndex >= sessions.length - 1}
              onClick={() => goToSession(sessionIndex + 1)}
            >
              <SkipForward className="h-3.5 w-3.5" /> Skip student
            </Button>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={onExit}>
              <X className="h-4 w-4" /> Exit
            </Button>
          </div>
        </CardContent>
        <div className="px-3 pb-3">
          <Progress
            value={(sessionIndex / Math.max(1, sessions.length)) * 100}
            className="h-1.5"
          />
        </div>
      </Card>

      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <TableSkeleton rows={4} columns={1} />
          ) : error ? (
            // A 403 here is a real answer, not an empty paper: grading scope is narrower than the
            // scope that produced the queue row. Show the server's reason and let the run continue.
            <div className="text-center py-8 space-y-2">
              <AlertTriangle className="mx-auto h-8 w-8 text-destructive/60" />
              <p className="text-sm font-medium text-destructive">
                Couldn&apos;t load {studentLabelOf(session)}&apos;s answers.
              </p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                {error instanceof Error && error.message
                  ? error.message
                  : "The server rejected the request."}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={sessionIndex >= sessions.length - 1}
                onClick={() => goToSession(sessionIndex + 1)}
              >
                <SkipForward className="h-4 w-4" /> Next student
              </Button>
            </div>
          ) : !active ? (
            <div className="text-center py-8 space-y-3">
              <CheckCircle2 className="mx-auto h-8 w-8 text-success/50" />
              <p className="text-sm text-muted-foreground">
                No answers on this attempt need a manual mark.
              </p>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={advance}>
                <ArrowRight className="h-4 w-4" /> Continue
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">
                    Answer {activeIndex + 1} of {gradable.length}
                  </span>
                  <Badge
                    variant="outline"
                    className={
                      isPending(active)
                        ? "bg-warning/15 text-warning border-warning/30 text-[10px]"
                        : "bg-success/15 text-success border-success/30 text-[10px]"
                    }
                  >
                    {isPending(active) ? "Needs a mark" : "Marked"}
                  </Badge>
                </div>
                <p className="text-sm font-medium">{active.questionText}</p>
                <div className="border p-3 rounded-lg bg-background">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">
                    Student&apos;s Answer
                  </p>
                  <p className="text-sm whitespace-pre-wrap">
                    {active.selectedAnswer || "(No answer provided)"}
                  </p>
                </div>
                {active.correctAnswers?.length > 0 && (
                  <div className="border p-3 rounded-lg bg-muted/20">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">
                      Reference / Model Answer
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{active.correctAnswers.join("\n")}</p>
                  </div>
                )}
              </div>

              <div className="space-y-4 md:border-l md:pl-4">
                <div className="space-y-1">
                  <Label className="text-xs">Points awarded (out of {maxPoints})</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      ref={pointsRef}
                      type="number"
                      min={0}
                      max={maxPoints}
                      step="any"
                      className="w-24"
                      placeholder={`0 – ${maxPoints}`}
                      value={draft.points}
                      disabled={grade.isPending}
                      onChange={(e) => patchDraft({ points: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void save();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={grade.isPending}
                      onClick={() => patchDraft({ points: String(maxPoints) })}
                    >
                      Full
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={grade.isPending}
                      onClick={() => patchDraft({ points: "0" })}
                    >
                      Zero
                    </Button>
                    {draft.points !== "" && maxPoints > 0 && Number.isFinite(Number(draft.points)) && (
                      <span className="text-sm font-semibold">
                        {Math.round((Number(draft.points) / maxPoints) * 100)}%
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Feedback (optional)</Label>
                  <Textarea
                    rows={4}
                    placeholder="Feedback for the student"
                    value={draft.note}
                    disabled={grade.isPending}
                    onChange={(e) => patchDraft({ note: e.target.value })}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button disabled={grade.isPending} onClick={() => void save()} className="gap-1.5">
                    {grade.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    Save &amp; next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={grade.isPending}
                    onClick={advance}
                  >
                    <SkipForward className="h-4 w-4" /> Skip answer
                  </Button>
                </div>

                <p className="text-[11px] text-muted-foreground">
                  <kbd className="px-1 py-0.5 border rounded">Enter</kbd> save &amp; next ·{" "}
                  <kbd className="px-1 py-0.5 border rounded">Ctrl</kbd>+
                  <kbd className="px-1 py-0.5 border rounded">Enter</kbd> save from the notes box ·{" "}
                  <kbd className="px-1 py-0.5 border rounded">Esc</kbd> exit
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual movement within the paper, for re-reading an answer already marked. */}
      {gradable.length > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="icon"
            disabled={activeIndex <= 0}
            onClick={() => setActiveResponseId(gradable[activeIndex - 1]?.responseId ?? null)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            {activeIndex + 1} / {gradable.length}
          </span>
          <Button
            variant="outline"
            size="icon"
            disabled={activeIndex >= gradable.length - 1}
            onClick={() => setActiveResponseId(gradable[activeIndex + 1]?.responseId ?? null)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
